import { useState } from 'react';
import { Check, X, Zap, Shield, Building2, HelpCircle } from 'lucide-react';

const APP_URL = 'https://app.karyva.com';

interface PricingPageProps {
  onNavigate: (page: string) => void;
}

// ── Plan data ─────────────────────────────────────────────────────────────────

// Feature splits below mirror the actual backend gating: only `require_plan`
// guards differentiate the paid tiers. Growth unlocks AI + executive dashboards
// + the integration breadth; Business adds the tamper-evident audit-log export.
// Everything else (custom fields, evidence, reporting/exports, guests, ACLs) is
// ungated and ships on every plan. Seat/project counts are NOT enforced anywhere
// in code yet, so we make no numeric promises until the seat-limit decision lands.
const PLANS = [
  {
    key: 'free',
    name: 'Free Trial',
    price: '$0',
    period: '14 days',
    tagline: 'The full core platform — no card required',
    cta: 'Start free trial',
    ctaUrl: `${APP_URL}/signup`,
    highlighted: false,
    features: [
      { text: 'Projects, portfolios & programs', included: true },
      { text: 'Tasks, evidence & time tracking', included: true },
      { text: 'Custom fields & configurable terminology', included: true },
      { text: 'Reports with PDF / XLSX / PPTX export', included: true },
      { text: 'Guest collaborators & sharing', included: true },
      { text: 'AI assistant & insights', included: false },
      { text: 'Integrations (Slack, GitHub, …)', included: false },
    ],
  },
  {
    key: 'starter',
    name: 'Starter',
    price: '$29',
    period: 'per month',
    tagline: 'Your trial, made permanent',
    cta: 'Get started',
    ctaUrl: `${APP_URL}/signup?plan=starter`,
    highlighted: false,
    features: [
      { text: 'Everything in the free trial', included: true },
      { text: 'Projects, portfolios & programs', included: true },
      { text: 'Custom fields, evidence & time tracking', included: true },
      { text: 'Reports with PDF / XLSX / PPTX export', included: true },
      { text: 'Guest collaborators & project ACLs', included: true },
      { text: 'AI assistant & insights', included: false },
      { text: 'Integrations (Slack, GitHub, …)', included: false },
    ],
  },
  {
    key: 'growth',
    name: 'Growth',
    price: '$79',
    period: 'per month',
    tagline: 'AI, dashboards & integrations',
    cta: 'Start with Growth',
    ctaUrl: `${APP_URL}/signup?plan=growth`,
    highlighted: true,
    badge: 'Most popular',
    features: [
      { text: 'Everything in Starter', included: true },
      { text: 'AI assistant, insights & predictions', included: true },
      { text: 'Executive command-center dashboards', included: true },
      { text: 'Slack, Teams, GitHub & Jira', included: true },
      { text: 'Email-in, calendar sync & cloud storage', included: true },
      { text: 'Custom notification channels', included: true },
      { text: 'Tamper-evident audit-log export', included: false },
    ],
  },
  {
    key: 'business',
    name: 'Business',
    price: '$199',
    period: 'per month',
    tagline: 'Compliance controls for regulated teams',
    cta: 'Get Business',
    ctaUrl: `${APP_URL}/signup?plan=business`,
    highlighted: false,
    features: [
      { text: 'Everything in Growth', included: true },
      { text: 'Tamper-evident audit-log export', included: true },
      { text: 'SOC 2 / compliance evidence trail', included: true },
      { text: 'Zero-trust tenant isolation', included: true },
      { text: 'GDPR data export & deletion', included: true },
      { text: 'Industry packs for regulated sectors', included: true },
    ],
  },
];

// ── Feature comparison ────────────────────────────────────────────────────────

// Each row reflects real product gating (backend `require_plan` + frontend
// PlanGate). Core, customisation, collaboration, reporting and the base
// security posture are ungated — they ship on every plan. Growth gates AI +
// dashboards + the integration breadth; Business gates the audit-log export.
const COMPARISON_ROWS = [
  { category: 'Core platform', feature: 'Projects, portfolios & programs', free: true, starter: true, growth: true, business: true },
  { category: 'Core platform', feature: 'Tasks, subtasks & custom statuses', free: true, starter: true, growth: true, business: true },
  { category: 'Core platform', feature: 'Time tracking & completion evidence', free: true, starter: true, growth: true, business: true },
  { category: 'Core platform', feature: 'Custom fields', free: true, starter: true, growth: true, business: true },
  { category: 'Core platform', feature: 'Configurable terminology & industry packs', free: true, starter: true, growth: true, business: true },
  { category: 'Core platform', feature: 'Reports + PDF / XLSX / PPTX export', free: true, starter: true, growth: true, business: true },
  { category: 'Collaboration', feature: 'Comments & @mentions', free: true, starter: true, growth: true, business: true },
  { category: 'Collaboration', feature: 'Guest collaborators & sharing', free: true, starter: true, growth: true, business: true },
  { category: 'Collaboration', feature: 'Project-level access controls (ACLs)', free: true, starter: true, growth: true, business: true },
  { category: 'AI & analytics', feature: 'AI assistant, insights & predictions', free: false, starter: false, growth: true, business: true },
  { category: 'AI & analytics', feature: 'Executive command-center dashboards', free: false, starter: false, growth: true, business: true },
  { category: 'Integrations', feature: 'Slack, Teams, GitHub & Jira', free: false, starter: false, growth: true, business: true },
  { category: 'Integrations', feature: 'Email-in mailboxes', free: false, starter: false, growth: true, business: true },
  { category: 'Integrations', feature: 'Calendar sync', free: false, starter: false, growth: true, business: true },
  { category: 'Integrations', feature: 'Cloud storage providers', free: false, starter: false, growth: true, business: true },
  { category: 'Integrations', feature: 'Custom notification channels', free: false, starter: false, growth: true, business: true },
  { category: 'Security & compliance', feature: 'Zero-trust tenant isolation', free: true, starter: true, growth: true, business: true },
  { category: 'Security & compliance', feature: 'GDPR data export & deletion', free: true, starter: true, growth: true, business: true },
  { category: 'Security & compliance', feature: 'Tamper-evident audit-log export', free: false, starter: false, growth: false, business: true },
];

// ── FAQ ───────────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: 'Do I need a credit card to start the trial?',
    a: 'No. Your 14-day trial includes the full core platform — projects, tasks, custom fields, reporting and guest collaboration — with no payment details required. AI, dashboards and integrations unlock on the Growth plan; you only add a card when you upgrade.',
  },
  {
    q: 'What happens to my data when the trial ends?',
    a: "Your data is safe — we never delete it. Your workspace goes into read-only mode so nothing is lost. Upgrade anytime to resume full access.",
  },
  {
    q: 'Can I change plans later?',
    a: 'Yes, upgrade or downgrade anytime from the billing settings inside the app. Upgrades take effect immediately. Downgrades take effect at the next billing cycle.',
  },
  {
    q: 'Does Karyva work for industries other than software?',
    a: 'That\'s exactly the point. Karyva ships pre-configured packs for construction, legal, healthcare, marketing agencies, finance, non-profit, and defense. The terminology, evidence requirements, and custom fields are all tailored to your industry.',
  },
  {
    q: 'What does "guest collaborators" mean?',
    a: 'Guests are external people (clients, contractors, inspectors) who can view and comment on specific projects without needing a full seat. They access the app via a secure invite link.',
  },
  {
    q: 'Do you offer enterprise or on-premise deployments?',
    a: 'Yes. We offer standalone deployments for organisations that need to run Karyva on their own infrastructure — including air-gapped environments for defense and classified workloads. Contact enterprise@karyva.com.',
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export function PricingPage({ onNavigate }: PricingPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showComparison, setShowComparison] = useState(false);

  // Group comparison rows by category
  const categories = [...new Set(COMPARISON_ROWS.map(r => r.category))];

  function renderCell(val: boolean | string) {
    if (val === true) return <Check size={16} className="text-green-500 mx-auto" />;
    if (val === false) return <X size={16} className="text-[#C4CDD5] mx-auto" />;
    return <span className="text-[#2A3B4C] text-sm">{val}</span>;
  }

  return (
    <div className="bg-white">

      {/* ── Hero ── */}
      <section className="pt-20 pb-12 px-6 text-center bg-gradient-to-b from-[#f8f9fa] to-white">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold mb-4 border border-violet-100">
            <Zap size={12} />
            Simple, transparent pricing
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#2A3B4C] leading-tight mb-4">
            Start free.<br />
            <span className="text-[#DAA520]">Scale when you're ready.</span>
          </h1>
          <p className="text-[#556B7C] text-lg">
            Every plan includes your industry pack, pre-configured templates, and
            a platform that speaks your language — not developer jargon.
          </p>
        </div>
      </section>

      {/* ── Plan cards ── */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map(plan => (
            <div
              key={plan.key}
              className={`relative rounded-2xl border p-6 flex flex-col gap-4 transition-shadow
                ${plan.highlighted
                  ? 'border-violet-400 shadow-xl shadow-violet-100 bg-violet-50/30'
                  : 'border-[#C4CDD5] bg-white hover:shadow-md'}`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-violet-600 text-white text-xs font-bold rounded-full whitespace-nowrap">
                  {plan.badge}
                </span>
              )}

              <div>
                <p className="text-xs font-bold text-[#556B7C] uppercase tracking-wider mb-1">{plan.name}</p>
                <div className="flex items-end gap-1">
                  <span className="text-3xl font-extrabold text-[#2A3B4C]">{plan.price}</span>
                  <span className="text-[#556B7C] text-sm mb-1">/{plan.period}</span>
                </div>
                <p className="text-xs text-[#556B7C] mt-1">{plan.tagline}</p>
              </div>

              <ul className="space-y-2 flex-1">
                {plan.features.map(f => (
                  <li key={f.text} className={`flex items-start gap-2 text-sm ${f.included ? 'text-[#2A3B4C]' : 'text-[#C4CDD5] line-through'}`}>
                    {f.included
                      ? <Check size={14} className="text-green-500 shrink-0 mt-0.5" />
                      : <X size={14} className="text-[#C4CDD5] shrink-0 mt-0.5" />}
                    {f.text}
                  </li>
                ))}
              </ul>

              <a
                href={plan.ctaUrl}
                className={`block text-center py-2.5 rounded-xl text-sm font-semibold transition-colors
                  ${plan.highlighted
                    ? 'bg-violet-600 hover:bg-violet-700 text-white'
                    : 'bg-[#2A3B4C] hover:bg-[#1a2942] text-white'}`}
              >
                {plan.cta}
              </a>
            </div>
          ))}
        </div>

        {/* Toggle full comparison */}
        <div className="text-center mt-10">
          <button
            onClick={() => setShowComparison(v => !v)}
            className="text-sm font-medium text-violet-600 hover:text-violet-800 transition-colors underline underline-offset-2"
          >
            {showComparison ? 'Hide' : 'See'} full feature comparison
          </button>
        </div>

        {/* Full comparison table */}
        {showComparison && (
          <div className="max-w-5xl mx-auto mt-8 overflow-x-auto rounded-2xl border border-[#C4CDD5]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#C4CDD5] bg-[#f8f9fa]">
                  <th className="text-left px-5 py-3 font-semibold text-[#2A3B4C] w-1/3">Feature</th>
                  {['Free Trial', 'Starter', 'Growth', 'Business'].map(h => (
                    <th key={h} className="px-4 py-3 text-center font-semibold text-[#2A3B4C]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <>
                    <tr key={cat} className="bg-violet-50/60">
                      <td colSpan={5} className="px-5 py-2 text-xs font-bold text-violet-700 uppercase tracking-wider">{cat}</td>
                    </tr>
                    {COMPARISON_ROWS.filter(r => r.category === cat).map(row => (
                      <tr key={row.feature} className="border-t border-[#f0f0f0] hover:bg-[#fafafa]">
                        <td className="px-5 py-3 text-[#2A3B4C]">{row.feature}</td>
                        <td className="px-4 py-3 text-center">{renderCell(row.free)}</td>
                        <td className="px-4 py-3 text-center">{renderCell(row.starter)}</td>
                        <td className="px-4 py-3 text-center bg-violet-50/30">{renderCell(row.growth)}</td>
                        <td className="px-4 py-3 text-center">{renderCell(row.business)}</td>
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Enterprise / standalone band ── */}
      <section className="bg-[#0a0f1e] py-16 px-6">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#DAA520]/20 flex items-center justify-center shrink-0">
              <Building2 size={22} className="text-[#DAA520]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Enterprise & on-premise</h2>
              <p className="text-[#90a1b9] text-sm max-w-lg">
                Dedicated deployment on your own infrastructure. SCIM/SSO, FedRAMP-ready,
                air-gapped support for classified environments, custom contracts, and
                dedicated onboarding. Pricing by negotiation.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {['SAML/SCIM', 'Air-gapped', 'Custom SLAs', 'On-prem Docker', 'Dedicated support'].map(tag => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-white/10 text-white/70 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <a
              href="mailto:enterprise@karyva.com"
              className="px-6 py-3 rounded-xl bg-[#DAA520] hover:bg-[#c4941b] text-black font-semibold text-sm transition-colors text-center"
            >
              Contact enterprise sales
            </a>
            <button
              onClick={() => onNavigate('contact')}
              className="px-6 py-2.5 rounded-xl border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm transition-colors"
            >
              Schedule a demo
            </button>
          </div>
        </div>
      </section>

      {/* ── Security trust bar ── */}
      <section className="bg-[#f8f9fa] py-10 px-6 border-y border-[#C4CDD5]">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-8 text-sm text-[#556B7C]">
          {[
            { icon: <Shield size={16} />, text: 'SOC2 Type II ready' },
            { icon: <Shield size={16} />, text: 'HIPAA-compliant mode' },
            { icon: <Shield size={16} />, text: 'Zero-trust architecture' },
            { icon: <Shield size={16} />, text: 'FedRAMP-ready deployment' },
            { icon: <Shield size={16} />, text: 'GDPR / data export + deletion' },
          ].map(item => (
            <div key={item.text} className="flex items-center gap-2 text-[#2A3B4C] font-medium">
              <span className="text-[#DAA520]">{item.icon}</span>
              {item.text}
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-[#2A3B4C] text-center mb-8">Frequently asked questions</h2>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="border border-[#C4CDD5] rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-semibold text-[#2A3B4C] hover:bg-[#f8f9fa] transition-colors"
                >
                  {faq.q}
                  <HelpCircle size={16} className={`shrink-0 ml-3 transition-colors ${openFaq === i ? 'text-violet-600' : 'text-[#C4CDD5]'}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-[#556B7C] leading-relaxed border-t border-[#f0f0f0]">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="py-16 px-6 bg-gradient-to-b from-violet-50 to-white text-center">
        <div className="max-w-xl mx-auto">
          <h2 className="text-2xl font-bold text-[#2A3B4C] mb-3">Ready to see it for your industry?</h2>
          <p className="text-[#556B7C] mb-6">Start your 14-day trial and have your workspace configured in under 60 seconds.</p>
          <a
            href={`${APP_URL}/signup`}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold transition-colors"
          >
            <Zap size={16} />
            Start free trial — no card needed
          </a>
        </div>
      </section>

    </div>
  );
}
